import { useMemo, type ReactNode } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'

export interface DataTableProps<T extends object> {
  columns: ColumnDef<T, unknown>[]
  rows: T[]
  loading?: boolean
  /** Number of skeleton rows to render while loading. */
  loadingRows?: number
  empty?: ReactNode
  className?: string
  /** Allow column-header click to sort. */
  sortable?: boolean
  initialSorting?: SortingState
  /** Optional row click handler - receives the row's data. */
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string | undefined
}

export function DataTable<T extends object>({
  columns,
  rows,
  loading,
  loadingRows = 5,
  empty,
  className,
  sortable = true,
  initialSorting = [],
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting)

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    enableSorting: sortable,
  })

  const colCount = columns.length

  const skeletonRows = useMemo(
    () =>
      Array.from({ length: loadingRows }).map((_, i) => (
        <TableRow key={`sk-${i}`}>
          {Array.from({ length: colCount }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      )),
    [loadingRows, colCount],
  )

  return (
    <div className={cn('overflow-hidden rounded-md border bg-card', className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => {
                const canSort = sortable && header.column.getCanSort()
                const sorted = header.column.getIsSorted()
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        className={cn(
                          'flex items-center gap-1 text-left text-inherit',
                          canSort && 'cursor-pointer select-none hover:opacity-80',
                          !canSort && 'cursor-default',
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort ? (
                          sorted === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )
                        ) : null}
                      </button>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading
            ? skeletonRows
            : table.getRowModel().rows.length === 0
              ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">
                      {empty ?? 'No records found.'}
                    </TableCell>
                  </TableRow>
                )
              : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={cn(
                        onRowClick && 'cursor-pointer',
                        rowClassName?.(row.original),
                      )}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
        </TableBody>
      </Table>
    </div>
  )
}
